// ════════════════════════════════════════════════════════════════════════════
//  features/renave/ConferenciaFiscalPainel.tsx
//  Resultado da conferência da NF-e do veículo para o REGIME NORMAL (Lucro
//  Presumido/Real). Bloqueios impedem enviar a chave ao RENAVE; avisos são
//  pontos para o contador (redução de base do ICMS, PIS/COFINS pela margem).
// ════════════════════════════════════════════════════════════════════════════

import { AlertTriangle, CheckCircle2, Info, XCircle } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { formatarMoeda, type ConferenciaFiscal } from './api';

export function ConferenciaFiscalPainel({ c }: { c: ConferenciaFiscal }) {
  const n = c.nota;
  return (
    <div className="space-y-3 rounded-md border p-3 text-sm">
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-medium">Conferência fiscal da NF-e de {c.evento === 'VENDA' ? 'venda' : 'entrada'}</span>
        <Badge variant="outline">{c.regime}</Badge>
        {n && (c.bloqueios.length === 0
          ? <Badge className="gap-1"><CheckCircle2 className="h-3 w-3" />Pode enviar ao RENAVE</Badge>
          : <Badge variant="destructive" className="gap-1"><XCircle className="h-3 w-3" />Corrija antes de enviar</Badge>)}
      </div>

      {n && (
        <dl className="grid gap-x-4 gap-y-1 text-xs sm:grid-cols-3">
          <Item k="NF-e" v={`nº ${n.numero} · ${new Date(n.dataEmissao).toLocaleDateString('pt-BR')}`} />
          <Item k="Destinatário" v={`${n.nomeDestinatario ?? '—'} (${n.documentoDestinatario ?? '—'})`} />
          <Item k="Valor do veículo" v={formatarMoeda(n.valorItem)} />
          <Item k="CFOP" v={n.cfop ?? '—'} />
          <Item k="ICMS" v={n.cst ? `CST ${n.cst} · base ${formatarMoeda(n.baseIcms)} · ${pct(n.aliquotaIcms)} · ${formatarMoeda(n.valorIcms)}` : `CSOSN ${n.csosn ?? '—'}`} />
          <Item k="PIS / COFINS" v={`${formatarMoeda(n.valorPis)} / ${formatarMoeda(n.valorCofins)}`} />
          <Item k="Chave" v={n.chave} mono className="sm:col-span-3" />
        </dl>
      )}

      {c.bloqueios.map(b => (
        <p key={b} className="flex gap-2 text-destructive"><XCircle className="mt-0.5 h-4 w-4 shrink-0" />{b}</p>
      ))}
      {c.avisos.map(a => (
        <p key={a} className="flex gap-2 text-amber-700 dark:text-amber-400"><AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />{a}</p>
      ))}

      {c.margem && (
        <div className="rounded bg-muted/50 p-2 text-xs">
          <p className="mb-1 flex items-center gap-1 font-medium"><Info className="h-3.5 w-3.5" />
            PIS/COFINS pela margem (art. 5º Lei 9.716/98) — estimativa para o contador
          </p>
          <p>
            Venda {formatarMoeda(c.margem.valorVenda)} − compra {formatarMoeda(c.margem.valorCompra)} = margem{' '}
            <strong>{formatarMoeda(c.margem.margem)}</strong> · PIS {pct(c.margem.aliquotaPis)} ={' '}
            <strong>{formatarMoeda(c.margem.pisSobreMargem)}</strong> · COFINS {pct(c.margem.aliquotaCofins)} ={' '}
            <strong>{formatarMoeda(c.margem.cofinsSobreMargem)}</strong>
          </p>
        </div>
      )}
    </div>
  );
}

const pct = (v: number) => `${v.toLocaleString('pt-BR', { maximumFractionDigits: 2 })}%`;

function Item({ k, v, mono, className }: { k: string; v: string; mono?: boolean; className?: string }) {
  return (
    <div className={className}>
      <dt className="text-muted-foreground">{k}</dt>
      <dd className={mono ? 'break-all font-mono' : ''}>{v}</dd>
    </div>
  );
}
